// ** React Imports
import { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

// ** Redux Imports
import { store } from "./redux/store";
import { Provider } from "react-redux";
import { authService } from "./@api-services/auth";
import { fetchPermissionCatalog, refreshUserPermissions } from "./redux/auth";

void store.dispatch(fetchPermissionCatalog());

if (authService.hasValidSession()) {
  void store.dispatch(refreshUserPermissions());
}

// ** Intl, CASL & ThemeColors Context
import ability from "./configs/acl/ability";
import { AbilityContext } from "./utility/context/Can";
import { ThemeContext } from "./utility/context/ThemeColors";

// ** ThemeConfig
import themeConfig from "./configs/themeConfig";

// ** Toast
import { Toaster } from "react-hot-toast";

// ** i18n
import "./configs/i18n";

// ** Spinner (Splash Screen)
import Spinner from "./@core/components/spinner/Fallback-spinner.tsx";

// ** Ripple Button
import "./@core/components/ripple-button";

// ** Fake Database — disabled by default (adds significant startup cost)
// Enable only when explicitly needed: VITE_ENABLE_MOCK=true
if (import.meta.env.VITE_ENABLE_MOCK === "true") {
  void import("./@fake-db");
}

// // ** PrismJS
// import "prismjs";
// import "prismjs/themes/prism-tomorrow.css";
// import "prismjs/components/prism-jsx.min";

// ** React Perfect Scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";

// ** React Hot Toast Styles
import "./@core/scss/react/libs/react-hot-toasts/react-hot-toasts.scss";

// ** SweetAlert2 theme styles (status/delete confirmations)
import "@styles/base/plugins/extensions/ext-component-sweet-alerts.scss";

// ** Core styles
import "./@core/assets/fonts/feather/iconfont.css";
import "./@core/scss/core.scss";

import "./assets/scss/style.scss";

import * as serviceWorker from "./serviceWorker";

// ** Lazy load app
const LazyApp = lazy(() => import("./App"));

const container = document.getElementById("root");
if (!container) {
  throw new Error("Failed to find the root element");
}
const root = createRoot(container);

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <Suspense fallback={<Spinner />}>
        <AbilityContext.Provider value={ability}>
          <ThemeContext>
            <LazyApp />
            <Toaster
              position={themeConfig.layout.toastPosition}
              toastOptions={{ className: "react-hot-toast" }}
            />
          </ThemeContext>
        </AbilityContext.Provider>
      </Suspense>
    </Provider>
  </BrowserRouter>
);

void serviceWorker.register();
